import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  IconButton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Service, Company, Individual, User, Branch } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' }
];

interface DayTasks {
  dayOfWeek: number;
  tasks: string[];
}

const ServiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Datos del formulario
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clientType, setClientType] = useState<'company' | 'individual'>('company');
  const [companyId, setCompanyId] = useState('');
  const [individualId, setIndividualId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [responsibleIds, setResponsibleIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [dayTasks, setDayTasks] = useState<DayTasks[]>(
    DAYS_OF_WEEK.map(day => ({ dayOfWeek: day.value, tasks: [] }))
  );

  // Datos de catálogos
  const [companies, setCompanies] = useState<Company[]>([]);
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  // Estado para agregar nueva tarea
  const [newTaskTexts, setNewTaskTexts] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    loadCatalogs();
    if (isEditMode && id) {
      loadService(id);
    }
  }, [id, isEditMode]);

  // Auto-select branch when client changes
  useEffect(() => {
    // Only auto-select if not in edit mode (or maybe always? User said "por defecto")
    // If in edit mode, initial load sets the branch. Changing client should update it.
    if (clientType === 'company' && companyId) {
      const comp = companies.find(c => c.id === companyId);
      if (comp && comp.branchId) {
        setBranchId(comp.branchId);
      }
    } else if (clientType === 'individual' && individualId) {
      const ind = individuals.find(i => i.id === individualId);
      if (ind && ind.branchId) {
        setBranchId(ind.branchId);
      }
    }
  }, [companyId, individualId, clientType, companies, individuals]);

  const loadCatalogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [companiesRes, individualsRes, usersRes, branchesRes] = await Promise.all([
        fetch(`${API_URL}/companies`, { headers }),
        fetch(`${API_URL}/individuals`, { headers }),
        fetch(`${API_URL}/users`, { headers }),
        fetch(`${API_URL}/branches`, { headers })
      ]);

      if (companiesRes.ok) setCompanies(await companiesRes.json());
      if (individualsRes.ok) setIndividuals(await individualsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (branchesRes.ok) setBranches(await branchesRes.json());
    } catch (err) {
      console.error('Error al cargar catálogos:', err);
    }
  };

  const loadService = async (serviceId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Error al cargar servicio');
      }

      const service: Service = await response.json();
      
      setName(service.name);
      setDescription(service.description || '');
      setIsActive(service.isActive);
      
      if (service.companyId) {
        setClientType('company');
        setCompanyId(service.companyId);
      } else if (service.individualId) {
        setClientType('individual');
        setIndividualId(service.individualId);
      }
      
      if (service.branchId) {
        setBranchId(service.branchId);
      }

      if (service.responsibles) {
        setResponsibleIds(service.responsibles.map(r => r.userId));
      }

      if (service.tasks) {
        const tasksByDay = DAYS_OF_WEEK.map(day => ({
          dayOfWeek: day.value,
          tasks: service.tasks!
            .filter(t => t.dayOfWeek === day.value)
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map(t => t.taskText)
        }));
        setDayTasks(tasksByDay);
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = (dayOfWeek: number) => {
    const taskText = newTaskTexts[dayOfWeek]?.trim();
    if (!taskText) return;

    setDayTasks(prev =>
      prev.map(day =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, tasks: [...day.tasks, taskText] }
          : day
      )
    );

    setNewTaskTexts(prev => ({ ...prev, [dayOfWeek]: '' }));
  };

  const handleRemoveTask = (dayOfWeek: number, taskIndex: number) => {
    setDayTasks(prev =>
      prev.map(day =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, tasks: day.tasks.filter((_, idx) => idx !== taskIndex) }
          : day
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validaciones
    if (!name.trim()) {
      setError('El nombre es requerido');
      return;
    }

    if (clientType === 'company' && !companyId) {
      setError('Debes seleccionar una empresa');
      return;
    }

    if (clientType === 'individual' && !individualId) {
      setError('Debes seleccionar un particular');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      
      const body = {
        name,
        description,
        companyId: clientType === 'company' ? companyId : null,
        individualId: clientType === 'individual' ? individualId : null,
        branchId: branchId || null,
        responsibleIds,
        isActive,
        tasks: dayTasks.filter(dt => dt.tasks.length > 0)
      };

      const url = isEditMode 
        ? `${API_URL}/services/${id}` 
        : `${API_URL}/services`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar servicio');
      }

      setSuccess(`Servicio ${isEditMode ? 'actualizado' : 'creado'} exitosamente`);
      
      setTimeout(() => {
        navigate('/services');
      }, 1500);
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message || 'Error al guardar servicio');
    } finally {
      setLoading(false);
    }
  };

  const getDayLabel = (dayOfWeek: number) => {
    return DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label || '';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Editar Servicio' : 'Nuevo Servicio'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Información General
          </Typography>

          <TextField
            label="Nombre del Servicio"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            margin="normal"
          />

          <TextField
            label="Descripción General"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            margin="normal"
            helperText="Descripción que verán los responsables y en el código QR"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Cliente</InputLabel>
            <Select
              value={clientType}
              onChange={(e) => {
                setClientType(e.target.value as 'company' | 'individual');
                setCompanyId('');
                setIndividualId('');
              }}
              label="Tipo de Cliente"
            >
              <MenuItem value="company">Empresa</MenuItem>
              <MenuItem value="individual">Particular</MenuItem>
            </Select>
          </FormControl>

          {clientType === 'company' ? (
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                label="Empresa"
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Particular</InputLabel>
              <Select
                value={individualId}
                onChange={(e) => setIndividualId(e.target.value)}
                label="Particular"
              >
                {individuals.map((individual) => (
                  <MenuItem key={individual.id} value={individual.id}>
                    {individual.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Sucursal</InputLabel>
            <Select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              label="Sucursal"
            >
              <MenuItem value="">
                <em>Ninguna</em>
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Responsables</InputLabel>
            <Select
              multiple
              value={responsibleIds}
              onChange={(e) => setResponsibleIds(e.target.value as string[])}
              input={<OutlinedInput label="Responsables" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((userId) => {
                    const user = users.find(u => u.id === userId);
                    return user ? (
                      <Chip
                        key={userId}
                        label={`${user.firstName} ${user.lastName}`}
                        size="small"
                      />
                    ) : null;
                  })}
                </Box>
              )}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isEditMode && (
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Servicio activo"
              sx={{ mt: 2 }}
            />
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Tareas por Día de la Semana
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Define qué tareas deben realizarse cada día de la semana
          </Typography>

          {DAYS_OF_WEEK.map((day) => {
            const dayData = dayTasks.find(dt => dt.dayOfWeek === day.value);
            const hasTasks = dayData && dayData.tasks.length > 0;

            return (
              <Accordion key={day.value}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography>{day.label}</Typography>
                    {hasTasks && (
                      <Chip 
                        label={`${dayData.tasks.length} tarea${dayData.tasks.length !== 1 ? 's' : ''}`} 
                        size="small" 
                        color="primary"
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {dayData && dayData.tasks.length > 0 && (
                      <List dense>
                        {dayData.tasks.map((task, idx) => (
                          <ListItem key={idx}>
                            <ListItemText primary={task} />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemoveTask(day.value, idx)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <TextField
                        size="small"
                        placeholder="Nueva tarea"
                        value={newTaskTexts[day.value] || ''}
                        onChange={(e) => setNewTaskTexts(prev => ({
                          ...prev,
                          [day.value]: e.target.value
                        }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTask(day.value);
                          }
                        }}
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddTask(day.value)}
                      >
                        Agregar
                      </Button>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/services')}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ServiceForm;





